import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Clock,
    Tag,
    Activity,
    Calculator,
    RadioTower,
    Folder,
    Loader2,
    AlertCircle,
    Thermometer,
    Signal,
    Phone,
    Battery
} from 'lucide-react';

// ============================================
// ✅ Вспомогательные функции форматирования
// ============================================
const getRejimRabot = (num) => {
    const map = { 0: 'Отключен', 1: 'Тепловычислитель', 2: 'Поверка', 3: 'Управление ИТП' };
    return map[num] || '—';
};

const getTimeIntegrirov = (num) => {
    return num + " мин" || '—';
};

// ============================================
// ✅ Главный компонент InfoTab
// ============================================
const InfoTab = ({ device }) => {
    const [deviceInfo, setDeviceInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadDeviceInfo();
    }, [device.name]);

    const loadDeviceInfo = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5022';

            const response = await axios.get(
                `${API_URL}/api/devices/aksioma01/${device.name}/info`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            console.log('📋 Аксиома-01 Info:', response.data);
            setDeviceInfo(response.data);
        } catch (err) {
            console.error('Ошибка загрузки информации:', err);
            setError('Не удалось загрузить информацию об устройстве');
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <Loader2 className="animate-spin h-8 w-8 text-blue-500 mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">Загрузка информации...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
            </div>
        );
    }

    if (!deviceInfo) {
        return (
            <div className="bg-slate-800 rounded-xl p-12 border border-slate-700 text-center">
                <div className="text-5xl mb-3">📭</div>
                <p className="text-slate-400">Нет данных</p>
            </div>
        );
    }

    // Группы информации как в Windows Forms
    const groups = [
        {
            name: 'Время и дата',
            icon: Clock,
            iconColor: 'text-blue-400',
            items: [
                { label: 'Данные от', value: deviceInfo.datetime || '—' }
            ]
        },
        {
            name: 'Идентификационные данные тепловычислителя',
            icon: Tag,
            iconColor: 'text-purple-400',
            items: [
                { label: 'Наименование', value: deviceInfo.id_name || '—' },
                { label: 'Серийный номер', value: deviceInfo.serial_number || '—' },
                { label: 'Версия метрологической части', value: deviceInfo.id_metro_serial_number || '—' },
                { label: 'Версия интерфейсной части', value: deviceInfo.id_iterf_serial_number || '—' },
                { label: 'Версия Boot загрузчика', value: deviceInfo.version_boot_loader || '—' },
                { label: 'Контрольная сумма (CRC16) метрологической части', value: deviceInfo.metro_crc || '—' }
            ]
        },
        {
            name: 'Статус тепловычислителя',
            icon: Activity,
            iconColor: 'text-green-400',
            items: [
                { label: 'Контрольная сумма (CRC16) настроек', value: deviceInfo.settings_crc || '—' },
                { label: 'Дата последних измен. настроек', value: deviceInfo.lastModDatetime || '—' },
                { label: 'Изменение параметров', value: deviceInfo.switch_save || '—' },
                { label: 'Источник питания', value: deviceInfo.source_power || '—' },
                {
                    label: 'Уровень заряда батареи',
                    value: deviceInfo.power_bat_percent !== null && deviceInfo.power_bat_percent !== undefined
                        ? `${deviceInfo.power_bat_percent} %`
                        : '—'
                },
                {
                    label: 'Температура устройства',
                    value: deviceInfo.temperatura_core !== null && deviceInfo.temperatura_core !== undefined
                        ? `${Math.round(deviceInfo.temperatura_core * 10) / 10} °C`
                        : '—'
                },
                { label: 'Режим работы', value: getRejimRabot(deviceInfo.work_function) }
            ]
        },
        {
            name: 'Алгоритм учета',
            icon: Calculator,
            iconColor: 'text-cyan-400',
            items: [
                { label: 'Схема измерений (СИ)', value: `№ ${deviceInfo.sis_count_si || 0}` },
                { label: 'Формулы расчета (ФТ)', value: `№ ${deviceInfo.sis_count_ft || 0}` },
                { label: 'Время интегрирования', value: getTimeIntegrirov(deviceInfo.time_integrirov) }
            ]
        },
        {
            name: 'GSM модем',
            icon: RadioTower,
            iconColor: 'text-indigo-400',
            items: [
                { label: 'Уровень сигнала', value: deviceInfo.at_csq ? `${deviceInfo.at_csq} %` : '—' },
                { label: 'Заряд батареи', value: deviceInfo.at_adc ? `${deviceInfo.at_adc} %` : '—' },
                {
                    label: 'Температура',
                    value: deviceInfo.at_cmte === -100 || deviceInfo.at_cmte === null || deviceInfo.at_cmte === undefined
                        ? 'Не определена'
                        : `${deviceInfo.at_cmte} °C`
                },
                { label: 'Оператор сети', value: deviceInfo.gsm_operator || '—' },
                { label: 'Номер телефона (без 8)', value: deviceInfo.gsm_number_tel || '—' },
                {
                    label: 'Баланс',
                    value: deviceInfo.at_balance === 0 || deviceInfo.at_balance === null || deviceInfo.at_balance === undefined
                        ? 'Не определен'
                        : `${deviceInfo.at_balance} руб`
                }
            ]
        },
        {
            name: 'Архив',
            icon: Folder,
            iconColor: 'text-orange-400',
            items: [
                { label: 'Количество записей', value: deviceInfo.count_magazin_zap || 0 },
                { label: 'Дата начала журнала', value: deviceInfo.startMagazinZapDatetime || '—' }
            ]
        }
    ];

    return (
        <div className="space-y-4">
            {groups.map((group, idx) => {
                const Icon = group.icon;
                return (
                    <div key={idx} className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                        <div className="bg-slate-700/50 px-4 sm:px-6 py-3 border-b border-slate-700">
                            <h3 className="text-white font-semibold flex items-center gap-2">
                                <Icon className={`w-5 h-5 ${group.iconColor}`} />
                                {group.name}
                            </h3>
                        </div>
                        <div className="divide-y divide-slate-700">
                            {group.items.map((item, itemIdx) => (
                                <div
                                    key={itemIdx}
                                    className="flex flex-col sm:flex-row sm:items-center px-4 sm:px-6 py-3 hover:bg-slate-700/20 transition"
                                >
                                    <div className="text-slate-400 text-sm sm:w-96 flex-shrink-0 mb-1 sm:mb-0">
                                        {item.label}
                                    </div>
                                    <div className="text-white font-medium text-sm sm:flex-1">
                                        {item.value}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default InfoTab;